import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Api from "../../services/Api";
import {getLanguage, showLanguageString} from "../../lang/helpers";
import {SurveyResultInterface, SurveyQuestionInterface, AiInsights, LinkRequestInterface, FeedbackType} from "./interfaces";
import SurveyResultSummary from "./SurveyResultSummary";
import SurveyResultArea from "./SurveyResultArea";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import iconCircleRings from "url:shared-assets/icons/white/circle-rings.svg";
import CustomButton from "../../components/CustomButton";
import Loading from "../../components/Loading";
import {scrollTop} from "../../helpers/scrolling";
import iconEmail from "url:shared-assets/icons/email_16x16.svg";
import iconCalendar from "url:shared-assets/icons/calendar.svg";
import iconEmailOpen from "url:shared-assets/icons/email-open.svg";
import iconAccount from "url:shared-assets/icons/account.svg";

interface ClientInterface {
  id: string;
  firstName: string;
  lastName: string;
}

interface RequestSummary {
  created: string;
  completed: string;
  sent: number;
  answered: number;
}

const SurveyResult = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [surveyId, setSurveyId] = useState<string>("");
  const [clientName, setClientName] = useState<string>("");
  const [otherRequests, setOtherRequests] = useState<Array<LinkRequestInterface>>([]);
  const [canCreateRequest, setCanCreateRequest] = useState(false);
  const [surveyResult, setSurveyResult] = useState<SurveyResultInterface>({
    categoryId: "",
    categoryName: "",
    categoryNameSv: "",
    scoreSelf: 0,
    scoreOther: 0,
    gap: 0,
    spreadQuestions: 0,
    spreadQuestionsNormalized: 0,
    spreadRespondents: 0,
    spreadRespondentsNormalized: 0,
    focusAreas: [],
    nrOtherResponses: 0,
    nrAnswerOptions: 5,
    answerOptions: {},
    languages: [],
    respondents: [],
  });
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(FeedbackType.FEEDBACK);
  const [aiInsights, setAiInsights] = useState<AiInsights>({
    summary: "N/A",
    strengths: "N/A",
    improvements: "N/A",
    focusAreas: [],
  })
  const [requestSummary, setRequestSummary] = useState<RequestSummary>({
    created: "",
    completed: "",
    sent: 0,
    answered: 0,
  });
  const [allQuestions, setAllQuestions] = useState<Array<SurveyQuestionInterface>>([]);
  const [focusAreasSorted, setFocusAreasSorted] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [client, setClient] = useState<ClientInterface | null>(null);
  const { id, page } = useParams();
  const coachView = window.location.pathname.startsWith("/clients/survey_result/");

  useEffect(() => {
    getData();
    setFocusAreasSorted(page ? page : "summary");
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
    }
  }, [id]);

  const onPop = () => {
    console.log('onPop')
    const urlParts = window.location.pathname.split("/");
    if (urlParts.length > 5) {
      const _page = urlParts[urlParts.length - 1];
      setFocusAreasSorted(_page);
    }

  }

  const changePage = (_page: string) => {
    let url = client ? "/clients/survey_result/" : "/dev-plan/survey/result/";
    url += id + "/" + _page;
    history.pushState(null, "", url);
    setFocusAreasSorted(_page);
    scrollTop();
  }

  const getData = () => {
    let url = coachView ? "coach/client/survey_result/" + id : "client/survey_result/" + id;
    api.get(url).then(response => {
      if (response.json.status === "ok") {
        setClient(response.json.client);
        setCanCreateRequest(response.json.canCreateRequest)
        setClientName(response.json.clientName);
        setSurveyResult(response.json.survey_result);
        setSurveyId(response.json.surveyId);
        setAiInsights(response.json.aiInsights);
        setOtherRequests(response.json.all_requests);
        setFeedbackType(response.json.feedbackType);
        setRequestSummary(response.json.requestSummary);
        const _allQuestions: Array<SurveyQuestionInterface> = [];
        for (const focusArea of response.json.survey_result.focusAreas) {
          for (const question of focusArea.questions) {
            question.focusAreaName = focusArea.name;
            question.focusArea = focusArea;
            _allQuestions.push(question);
          }
        }
        setAllQuestions(_allQuestions);
      } else if (response.json.error) {
        setError(response.json.error);
      } else {
        setError("Unknown error");
      }
      setLoading(false);
    });
  }

  if (loading) {
    return <Loading txt={lang === "sv" ? "Laddar rapporten, det kan ta ett tag" : "Loading report, this may take a while"}/>;
  }

  if (error.length > 0) {
    return (<div className="mt-4">Error: {error}</div>);
  }

  const showCrumbs = () => {
    let mainMain = showLanguageString(lang, surveyResult.categoryNameSv, surveyResult.categoryName) + " "
      + t("insights-report");
    let crumbs;
    if (focusAreasSorted === "summary") {
      if (client) {
        crumbs = [
          {name: lang === "sv" ? "Hem" : "Home", url: "/"},
          {name: t("my-clients"), url: "/clients"},
          {name: client.firstName + " " + client.lastName, url: "/clients/" + client.id},
          {name: mainMain},
        ]
      } else {
        crumbs = [
          {name: t("assessments"), url: "/assessments"},
          {name: mainMain},
        ]
      }

    } else {
      const _title = lang === "sv" ? "Resultat per område": "Result per Area";
      if (client) {
        crumbs = [
          {name: lang === "sv" ? "Hem" : "Home", url: "/"},
          {name: t("my-clients"), url: "/clients"},
          {name: client.firstName + " " + client.lastName, url: "/clients/" + client.id},
          {name: mainMain, clickFunc: () => changePage("summary")},
          {name: _title},
        ]
      } else {
        crumbs = [
          {name: t("assessments"), url: "/assessments"},
          {name: mainMain, clickFunc: () => changePage("summary")},
          {name: _title},
        ]
      }
    }
    return (
      <LocalBreadcrumbs crumbs={crumbs}/>
    )

  }

  return (
    <div className="main-one-col">

      <div className="bg-indigo-1000 main-fixed px-4">
        {showCrumbs()}
        <div className="my-4 flex items-center justify-center">
          <div className="h-4 w-4 rounded-full bg-peach-500"></div>
          <div className="text-sm ml-2 text-gray-100">{t("self-assessment")}</div>
          <div className="h-4 w-4 rounded-full bg-peach-300 ml-4"></div>
          <div className="text-sm ml-2 text-gray-100">Feedback</div>
        </div>
      </div>

      <div style={{paddingTop: 50}}>&nbsp;</div>

      {
        focusAreasSorted === "summary" ? (
          <SurveyResultSummary allQuestions={allQuestions} surveyResult={surveyResult} lang={lang} t={t}
                               aiInsights={aiInsights} clientName={clientName}
                               otherRequests={otherRequests}
                               canCreateRequest={canCreateRequest}
                               feedbackType={feedbackType}
                               setFocusAreasSorted={changePage} surveyId={surveyId}/>
        ) : (
          <SurveyResultArea surveyResult={surveyResult} sorting={focusAreasSorted} aiInsights={aiInsights} t={t}
                            clientName={clientName} lang={lang} feedbackType={feedbackType}/>
        )
      }

      {
        !coachView &&
          <>
          <div className="mt-4 bg-indigo-900 rounded-md p-4 mb-4 border-indigo-800 border-1">
            <div className="z-title1">
              {t("details")}
            </div>

            <div className="mt-2 flex items-center">
                <img src={iconEmail} alt="sent" className="h-4 w-4 mr-2"/>
                <div className="z-title3">{t("request-sent")}:</div>
                <div className="text-sm text-gray-400">&nbsp;{requestSummary.created}</div>
            </div>

            <div className="mt-2 flex items-center">
                <img src={iconCalendar} alt="cal" className="h-4 w-4 mr-2"/>
                <div className="z-title3">{t("result-created-at")}:&nbsp;</div>
                <div className="text-sm text-gray-400">
                  {requestSummary.completed}
                </div>
            </div>

            <div className="mt-2 flex items-center">
                <img src={iconEmailOpen} alt="open" className="h-4 w-4 mr-2"/>
                <div className="z-title3">{t("received")}:</div>
                <div className="text-sm text-gray-400">
                    &nbsp;{requestSummary.answered} {t("out-of")} {requestSummary.sent}
                </div>
            </div>

            <div className="mt-2 flex items-center">
                <img src={iconAccount} alt="who" className="h-4 w-4 mr-2"/>
                <div className="z-title3">{t("requested-from")}:&nbsp;</div>
                <div className="text-sm text-gray-400">
                  {surveyResult.respondents.map((respondent, i) => (
                    <span key={"resp_" + i}>
                    {i > 0 ? ", " : ""}
                      {respondent.name}
                  </span>
                  ))}
                </div>
            </div>
          </div>
            
{/* END */}
            <div className="mt-10 text-center">
              <img src={iconCircleRings} alt="rings" className="mx-auto h-8 w-8"/>

              <div className="mt-4 z-heading1">
                {lang === "sv" ? "Dags att välja ett mål" : "Time to pick a goal"}
              </div>

              <div className="mt-4 text-gray-300 mx-auto" style={{width: 300}}>
                {lang === "sv" ? "Vi har förberett förslag på personliga mål till dig, baserat på ditt resultat." :
                  "We've prepared personlized goal suggestions based on your result."}
              </div>

              <div className="mt-4">
                <CustomButton buttonType="blueBase"
                              onClick={() => navigate('/dev-plan#drafts_' + surveyId)}
                              style={{width: "unset"}} className="mx-auto">
                  {lang === "sv" ? "Välj ett mål" : "Pick your goal"}
                </CustomButton>
              </div>
            </div>
          </>
      }


      <div className="mt-4">&nbsp;</div>
    </div>
  )
}

export default SurveyResult;